import React, { useState, useEffect } from 'react';
import { User, Phone, Shield, CreditCard, Bell, Lock } from 'lucide-react';
import { Container } from '../components/layout/Container';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Spinner } from '../components/common/Spinner';
import { useAuth } from '../context/AuthContext';
import { usePayoutSettings, useAddPayoutSetting, useDeletePayoutSetting, useBanks } from '../hooks/useWallet';
import { authService } from '../services/authService';
import api from '../config/api';
import { PAYOUT_METHODS } from '../utils/constants';
import toast from 'react-hot-toast';

export const Settings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { data: payoutSettingsData, isLoading: payoutLoading } = usePayoutSettings();
  const addPayoutSetting = useAddPayoutSetting();
  const deletePayoutSetting = useDeletePayoutSetting();
  
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfaEnabled || false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('BANK_TRANSFER');
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [country] = useState(user?.countryCode || '');
  const [phone, setPhone] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);

  const { data: banksData, isLoading: banksLoading } = useBanks(country);
  const rawBanks = banksData?.data || banksData || [];
  const banks = Array.isArray(rawBanks) ? rawBanks : [];
  const payoutSettings = payoutSettingsData?.data || [];

  // Auto-verify bank account
  useEffect(() => {
    if (bankCode && accountNumber.length >= 10) {
      setVerifyingAccount(true);
      setAccountName('');
      const timer = setTimeout(async () => {
        try {
          const res = await api.get('/wallet/verify-account', { params: { bankCode, accountNumber } });
          setAccountName(res.data?.data?.accountName || '');
        } catch { setAccountName(''); }
        finally { setVerifyingAccount(false); }
      }, 300);
      return () => clearTimeout(timer);
    } else { setAccountName(''); }
  }, [bankCode, accountNumber]);

  const handleAddPayout = async () => {
    if (payoutMethod === 'BANK_TRANSFER') {
      await addPayoutSetting.mutateAsync({ method: 'BANK_TRANSFER', accountName, accountNumber, bankName, bankCode, country });
      setAccountName(''); setAccountNumber(''); setBankCode(''); setBankName('');
    } else {
      await addPayoutSetting.mutateAsync({ method: 'PAYPAL', paypalEmail, country });
      setPaypalEmail('');
    }
  };

  const handleMFAToggle = () => { toast.success('MFA settings will be updated'); setMfaEnabled(!mfaEnabled); };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) { toast.error('Please fill in both fields'); return; }
    if (newPassword.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setChangingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword('');
    } catch (error: any) { toast.error(error.response?.data?.message || 'Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  const handleUpdatePhone = async () => {
    if (!phone) { toast.error('Please enter a phone number'); return; }
    setIsUpdatingPhone(true);
    try {
      const response = await authService.updatePhone(phone);
      if (response.data?.user) updateUser(response.data.user);
      toast.success('Phone number updated. Please verify it.');
      setIsEditingPhone(false); setIsVerifyingPhone(true);
    } catch (error: any) { toast.error(error.response?.data?.message || 'Failed to update phone number'); }
    finally { setIsUpdatingPhone(false); }
  };

  const handleSendVerification = async () => {
    setIsSendingOtp(true);
    try { await authService.sendPhoneVerification(); toast.success('Verification code sent to your phone'); setIsVerifyingPhone(true); }
    catch (error: any) { toast.error(error.response?.data?.message || 'Failed to send verification code'); }
    finally { setIsSendingOtp(false); }
  };

  const handleVerifyPhone = async () => {
    if (!phoneOtp) { toast.error('Please enter the verification code'); return; }
    setIsSubmittingOtp(true);
    try {
      const response = await authService.verifyPhone(phoneOtp);
      if (response.data?.user) updateUser(response.data.user);
      toast.success('Phone number verified successfully'); setPhoneOtp(''); setIsVerifyingPhone(false);
    } catch (error: any) { toast.error(error.response?.data?.message || 'Verification failed'); }
    finally { setIsSubmittingOtp(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
      <Container className="py-10" size="md">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>

        <div className="space-y-5">
          {/* Account Info */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <User className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Account Information</h2>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input label="First Name" value={user?.firstName} readOnly />
                <Input label="Last Name" value={user?.lastName} readOnly />
              </div>
              <Input label="Email" value={user?.email} readOnly />
              <Input label="Affiliate Code" value={user?.affiliateCode} readOnly />
            </div>
          </Card>

          {/* Phone */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Phone className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Phone Number</h2>
            </div>
            <div className="space-y-3">
              {!isEditingPhone && !isVerifyingPhone ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{user?.phone || 'No phone number on file'}</p>
                    {user?.phone && (
                      <p className="text-xs mt-0.5">
                        {user?.phoneVerified
                          ? <span className="text-emerald-600">✓ Verified</span>
                          : <span className="text-amber-600">Not verified</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setPhone(user?.phone || ''); setIsEditingPhone(true); }}>
                      {user?.phone ? 'Update' : 'Add Phone'}
                    </Button>
                    {user?.phone && !user?.phoneVerified && (
                      <Button size="sm" onClick={handleSendVerification} isLoading={isSendingOtp}>Verify</Button>
                    )}
                  </div>
                </div>
              ) : isEditingPhone ? (
                <>
                  <Input label="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1234567890" helperText="Include country code (e.g., +1 for US)" />
                  <div className="flex gap-2">
                    <Button onClick={handleUpdatePhone} isLoading={isUpdatingPhone}>Save</Button>
                    <Button variant="outline" onClick={() => { setIsEditingPhone(false); setPhone(''); }}>Cancel</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Enter the verification code sent to your phone</p>
                  <Input label="Verification Code" value={phoneOtp} onChange={(e) => setPhoneOtp(e.target.value)} placeholder="123456" />
                  <div className="flex gap-2">
                    <Button onClick={handleVerifyPhone} isLoading={isSubmittingOtp}>Verify</Button>
                    <Button variant="outline" onClick={handleSendVerification} isLoading={isSendingOtp}>Resend</Button>
                    <Button variant="outline" onClick={() => { setIsVerifyingPhone(false); setPhoneOtp(''); }}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Security */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Security</h2>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div>
                <p className="font-medium text-sm text-gray-900 dark:text-gray-100">Multi-Factor Authentication (MFA)</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
              </div>
              <Button variant={mfaEnabled ? 'danger' : 'primary'} size="sm" onClick={handleMFAToggle}>
                {mfaEnabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </Card>

          {/* Change Password */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Change Password</h2>
            </div>
            <div className="space-y-3">
              <Input label="Current Password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
              <Input label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
              <Button fullWidth onClick={handleChangePassword} isLoading={changingPassword}
                disabled={!currentPassword || !newPassword}>
                Change Password
              </Button>
            </div>
          </Card>

          {/* Payout Settings */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Payout Settings</h2>
            </div>
            
            {payoutLoading ? (
              <Spinner />
            ) : payoutSettings.length > 0 && (
              <div className="mb-5 space-y-2">
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400">Configured Methods</h3>
                {payoutSettings.map((setting) => (
                  <div key={setting.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <div>
                      <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{setting.method === 'BANK_TRANSFER' ? 'Bank Transfer' : 'PayPal'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {setting.method === 'BANK_TRANSFER' ? `${setting.bankName} - ${setting.accountNumber}` : setting.paypalEmail}
                      </p>
                    </div>
                    <Button variant="danger" size="sm" onClick={() => deletePayoutSetting.mutateAsync(setting.id)}>Remove</Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <Select label="Payout Method" value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} options={PAYOUT_METHODS} />
              {payoutMethod === 'BANK_TRANSFER' ? (
                <>
                  <Input label="Account Number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="1234567890" />
                  {verifyingAccount && <p className="text-xs text-gray-400">Verifying account...</p>}
                  {!verifyingAccount && accountName ? (
                    <div className="bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-100 dark:border-emerald-800 rounded-xl p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Account Name</p>
                      <p className="text-sm font-bold text-emerald-600 mt-0.5">{accountName}</p>
                    </div>
                  ) : !verifyingAccount && bankCode && accountNumber.length >= 10 ? (
                    <p className="text-xs text-red-600">Could not verify account</p>
                  ) : null}
                  <Select label="Bank" value={bankCode}
                    onChange={(e) => { setBankCode(e.target.value); const bank = banks.find((b: any) => b.code === e.target.value); if (bank) setBankName(bank.name); }}
                    options={[
                      { value: '', label: banksLoading ? 'Loading banks...' : banks.length === 0 ? 'No banks available' : 'Select bank' },
                      ...banks.map((b: any) => ({ value: b.code, label: b.name })),
                    ]}
                    disabled={banksLoading}
                  />
                </>
              ) : (
                <Input label="PayPal Email" type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} placeholder="your@paypal.com" />
              )}
              <Button fullWidth onClick={handleAddPayout} isLoading={addPayoutSetting.isPending}
                disabled={payoutMethod === 'BANK_TRANSFER' ? !accountName || !accountNumber || !bankCode : !paypalEmail}>
                Add Payout Method
              </Button>
            </div>
          </Card>

          {/* Notifications */}
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Bell className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Notifications</h2>
            </div>
            <div className="space-y-1">
              {[
                { label: 'Email Notifications', desc: 'Receive notifications via email', show: true },
                { label: 'SMS Notifications', desc: 'Receive notifications via SMS (verified phone required)', show: !!(user?.phone && user?.phoneVerified) },
                { label: 'Draw Win Notifications', desc: 'Get notified when you win a draw', show: true },
                { label: 'Withdrawal Notifications', desc: 'Get notified about withdrawal status', show: true },
                { label: 'Payment Settings Updates', desc: 'Get notified when payment settings change', show: true },
              ].filter(n => n.show).map((notification) => (
                <div key={notification.label} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <div>
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{notification.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{notification.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              ))}
              {user?.phone && !user?.phoneVerified && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-100 dark:border-amber-800 rounded-xl mt-2">
                  <p className="text-xs text-amber-700 dark:text-amber-400">Verify your phone number to enable SMS notifications</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </Container>
    </div>
  );
};
